let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect } from 'react';
import {


  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Bulletproof icon imports with fallbacks
const FallbackIcon = ({ name, size = 24, color = '#666', style, ...props }) => (
  <View 
    style={[
      {
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: size / 2,
        opacity: 0.3
      },
      style
    ]}
    {...props}
  />
);

let Ionicons = FallbackIcon;
let FontAwesome5 = FallbackIcon;
let MaterialIcons = FallbackIcon;

try {
  const iconsModule = require('@expo/vector-icons');
  if (iconsModule) {
    if (iconsModule.Ionicons && typeof iconsModule.Ionicons === 'function') {
      Ionicons = iconsModule.Ionicons;
    }
    if (iconsModule.FontAwesome5 && typeof iconsModule.FontAwesome5 === 'function') {
      FontAwesome5 = iconsModule.FontAwesome5;
    }
    if (iconsModule.MaterialIcons && typeof iconsModule.MaterialIcons === 'function') {
      MaterialIcons = iconsModule.MaterialIcons;
    }
  }
} catch (error) {
  console.warn('@expo/vector-icons not available, using fallback');
}

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    removeItem: async (key) => {}
  };
}

// Bulletproof service imports with fallbacks - NO DUPLICATE DECLARATIONS
class MockCertificationService {
  async initialize() { return true; }
  
  async addCertification(userId, certificationData) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        success: true,
        message: 'Certification added successfully',
        data: {
          id: Date.now(),
          ...certificationData
        }
      });
    }, 1000));
  }
  
  validateCertificationData(data) {
    const errors = {};
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Certification name is required';
    }
    if (!data.issuingOrganization || data.issuingOrganization.trim() === '') {
      errors.issuingOrganization = 'Issuing organization is required';
    }
    if (!data.issueDate || data.issueDate.trim() === '') {
      errors.issueDate = 'Issue date is required';
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  }
}

class MockUnifiedService {
  async initialize() { return true; }
  async trackUserActivity() { return { success: true }; }
}

class MockDashboardService {
  async initialize() { return true; }
  async updateDashboardMetrics() { return { success: true }; }
}

// Import services with fallbacks - SINGLE DECLARATION
let CertificationService = MockCertificationService;
let UnifiedService = MockUnifiedService;
let DashboardService = MockDashboardService;

try {
  const certificationModule = require('../../services/CertificationManagementService');
  if (certificationModule && certificationModule.default) {
    CertificationService = certificationModule.default;
  } else if (certificationModule && typeof certificationModule === 'function') {
    CertificationService = certificationModule;
  }
} catch (error) {
  console.warn('CertificationManagementService not available, using mock');
}

try {
  const unifiedModule = require('../../services/UnifiedIntegrationService');
  if (unifiedModule && unifiedModule.default) {
    UnifiedService = unifiedModule.default;
  } else if (unifiedModule && typeof unifiedModule === 'function') {
    UnifiedService = unifiedModule;
  }
} catch (error) {
  console.warn('UnifiedIntegrationService not available, using mock');
}

try {
  const dashboardModule = require('../../services/DashboardIntegrationService');
  if (dashboardModule && dashboardModule.default) {
    DashboardService = dashboardModule.default;
  } else if (dashboardModule && typeof dashboardModule === 'function') {
    DashboardService = dashboardModule;
  }
} catch (error) {
  console.warn('DashboardIntegrationService not available, using mock');
}

const { width, height } = Dimensions.get('window');

const AddCertification = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Services - USING RENAMED VARIABLES
  const [certificationService] = useState(() => new CertificationService());
  const [unifiedService] = useState(() => new UnifiedService());
  const [dashboardService] = useState(() => new DashboardService());

  // State management
  const [certification, setCertification] = useState({
    name: '',
    issuingOrganization: '',
    issueDate: '',
    expirationDate: '',
    credentialId: '',
    credentialUrl: '',
    description: '',
    skills: [''],
    doesNotExpire: false,
    verified: false,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Certification categories
  const certificationCategories = [
    'Technology',
    'Project Management',
    'Cloud Computing',
    'Data Science',
    'Cybersecurity',
    'Digital Marketing',
    'Design',
    'Business Analysis',
    'Quality Assurance',
    'DevOps',
    'Mobile Development',
    'Other'
  ];

  // Popular certification providers
  const certificationProviders = [
    'Microsoft',
    'Amazon Web Services (AWS)',
    'Google',
    'Oracle',
    'Cisco',
    'CompTIA',
    'PMI (Project Management Institute)',
    'Salesforce',
    'Adobe',
    'IBM',
    'Coursera',
    'Udacity',
    'edX',
    'Other'
  ];

  useEffect(() => {
    initializeServices();
  }, []);

  useEffect(() => {
    // Check if there are changes
    const initialState = {
      name: '',
      issuingOrganization: '',
      issueDate: '',
      expirationDate: '',
      credentialId: '',
      credentialUrl: '',
      description: '',
      skills: [''],
      doesNotExpire: false,
      verified: false,
    };
    const hasChangesDetected = JSON.stringify(certification) !== JSON.stringify(initialState);
    setHasChanges(hasChangesDetected);
  }, [certification]);

  const initializeServices = async () => {
    try {
      await Promise.all([
        certificationService.initialize().catch(e => console.warn('Certification service init failed:', e)),
        unifiedService.initialize().catch(e => console.warn('Unified service init failed:', e)),
        dashboardService.initialize().catch(e => console.warn('Dashboard service init failed:', e))
      ]);

      // Track screen view
      try {
        await unifiedService.trackUserActivity('add_certification_viewed');
      } catch (error) {
        console.warn('Failed to track activity:', error);
      }
    } catch (error) {
      console.error('Error initializing services:', error);
    }
  };

  const validateInput = () => {
    const validation = certificationService.validateCertificationData(certification);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSaveCertification = async () => {
    console.log('Save certification initiated...');
    
    if (!validateInput()) {
      Alert.alert('Validation Error', 'Please correct the errors in the form.');
      return;
    }

    setSaving(true);
    
    try {
      const userId = 'demo_user'; // In real app, get from auth context
      const result = await certificationService.addCertification(userId, certification);
      
      if (result.success) {
        // Save to cache
        await AsyncStorage.setItem(`professional_${_profUserId}_newCertification`, JSON.stringify(certification));
        
        // Track activity
        try {
          await unifiedService.trackUserActivity('certification_added');
          await dashboardService.updateDashboardMetrics('certification_added', { userId });
        } catch (error) {
          console.warn('Failed to track activity:', error);
        }
        
        // Show success message and navigate back
        Alert.alert(
          'Success',
          result.message || 'Certification added successfully!',
          [
            {
              text: 'OK',
              onPress: () => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate('Profile');
                }
              }
            }
          ]
        );
      } else {
        throw new Error(result.error || 'Failed to add certification');
      }
      
    } catch (error) {
      console.error('Error saving certification:', error);
      Alert.alert('Error', error.message || 'Failed to add certification. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBackPress = () => {
    if (hasChanges) {
      Alert.alert(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        [
          { text: 'Keep Editing', style: 'cancel' },
          { 
            text: 'Discard', 
            style: 'destructive', 
            onPress: () => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('Profile');
              }
            }
          }
        ]
      );
    } else {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Profile');
      }
    }
  };

  const updateCertification = (field, value) => {
    setCertification({ ...certification, [field]: value });
  };

  const addSkill = () => {
    setCertification({
      ...certification,
      skills: [...certification.skills, '']
    });
  };

  const updateSkill = (index, value) => {
    const newSkills = [...certification.skills];
    newSkills[index] = value;
    setCertification({ ...certification, skills: newSkills });
  };

  const removeSkill = (index) => {
    if (certification.skills.length > 1) {
      const newSkills = certification.skills.filter((_, i) => i !== index);
      setCertification({ ...certification, skills: newSkills });
    }
  };

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <StatusBar barStyle="dark-content" backgroundColor="#fff" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
          <Ionicons name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>Add Certification</Text>
        
        <View style={styles.headerPlaceholder} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Basic Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Certification Details</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Certification Name *</Text>
            <TextInput
              style={[styles.input, errors.name && styles.inputError]}
              value={certification.name}
              onChangeText={(text) => updateCertification('name', text)}
              placeholder="e.g., AWS Certified Solutions Architect"
              autoCapitalize="words"
            />
            {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Issuing Organization *</Text>
            <TextInput
              style={[styles.input, errors.issuingOrganization && styles.inputError]}
              value={certification.issuingOrganization}
              onChangeText={(text) => updateCertification('issuingOrganization', text)}
              placeholder="e.g., Amazon Web Services"
              autoCapitalize="words"
            />
            {errors.issuingOrganization && <Text style={styles.errorText}>{errors.issuingOrganization}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Popular Providers</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.providerScroll}>
              {certificationProviders.map((provider) => (
                <TouchableOpacity
                  key={provider}
                  style={[
                    styles.providerChip,
                    certification.issuingOrganization === provider && styles.providerChipSelected
                  ]}
                  onPress={() => updateCertification('issuingOrganization', provider)}
                >
                  <Text style={[
                    styles.providerChipText,
                    certification.issuingOrganization === provider && styles.providerChipTextSelected
                  ]}>
                    {provider}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>

        {/* Dates */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Certification Dates</Text>
          
          <View style={styles.dateRow}>
            <View style={styles.dateContainer}>
              <Text style={styles.label}>Issue Date *</Text>
              <TextInput
                style={[styles.input, errors.issueDate && styles.inputError]}
                value={certification.issueDate}
                onChangeText={(text) => updateCertification('issueDate', text)}
                placeholder="MM/YYYY"
              />
              {errors.issueDate && <Text style={styles.errorText}>{errors.issueDate}</Text>}
            </View>
            
            <View style={styles.dateContainer}>
              <Text style={styles.label}>Expiration Date</Text>
              <TextInput
                style={[styles.input, certification.doesNotExpire && styles.inputDisabled]}
                value={certification.doesNotExpire ? 'Never' : certification.expirationDate}
                onChangeText={(text) => updateCertification('expirationDate', text)}
                placeholder="MM/YYYY"
                editable={!certification.doesNotExpire}
              />
            </View>
          </View>

          <TouchableOpacity 
            style={styles.checkboxContainer}
            onPress={() => updateCertification('doesNotExpire', !certification.doesNotExpire)}
          >
            <View style={[styles.checkbox, certification.doesNotExpire && styles.checkboxChecked]}>
              {certification.doesNotExpire && (
                <Ionicons name="checkmark" size={16} color="#fff" />
              )}
            </View>
            <Text style={styles.checkboxLabel}>This certification does not expire</Text>
          </TouchableOpacity>
        </View>

        {/* Credential Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Credential Information</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Credential ID</Text>
            <TextInput
              style={styles.input}
              value={certification.credentialId}
              onChangeText={(text) => updateCertification('credentialId', text)}
              placeholder="e.g., AWS-SAA-123456"
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Credential URL</Text>
            <TextInput
              style={styles.input}
              value={certification.credentialUrl}
              onChangeText={(text) => updateCertification('credentialUrl', text)}
              placeholder="https://..."
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          <TouchableOpacity 
            style={styles.checkboxContainer}
            onPress={() => updateCertification('verified', !certification.verified)}
          >
            <View style={[styles.checkbox, certification.verified && styles.checkboxChecked]}>
              {certification.verified && (
                <Ionicons name="checkmark" size={16} color="#fff" />
              )}
            </View>
            <Text style={styles.checkboxLabel}>Verified certification</Text>
          </TouchableOpacity>
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Description</Text>
          
          <View style={styles.inputContainer}>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={certification.description}
              onChangeText={(text) => updateCertification('description', text)}
              placeholder="Describe what this certification covers, key skills learned, and how it applies to your work..."
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>
        </View>

        {/* Related Skills */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Related Skills</Text>
          
          {certification.skills.map((skill, index) => (
            <View key={index} style={styles.dynamicInputContainer}>
              <TextInput
                style={styles.input}
                value={skill}
                onChangeText={(text) => updateSkill(index, text)}
                placeholder={`Skill ${index + 1}`}
                autoCapitalize="words"
              />
              <View style={styles.dynamicInputActions}>
                {certification.skills.length > 1 && (
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeSkill(index)}
                  >
                    <Ionicons name="remove-circle" size={20} color="#f44336" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
          
          <TouchableOpacity style={styles.addButton} onPress={addSkill}>
            <Ionicons name="add-circle" size={20} color="#4CAF50" />
            <Text style={styles.addButtonText}>Add Skill</Text>
          </TouchableOpacity>
        </View>

        {/* Certification Categories */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Category</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
            {certificationCategories.map((category) => (
              <TouchableOpacity
                key={category}
                style={[
                  styles.categoryChip,
                  certification.category === category && styles.categoryChipSelected
                ]}
                onPress={() => updateCertification('category', category)}
              >
                <Text style={[
                  styles.categoryChipText,
                  certification.category === category && styles.categoryChipTextSelected
                ]}>
                  {category}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, (!hasChanges || saving) && styles.saveButtonDisabled]}
          onPress={handleSaveCertification}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>Add Certification</Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  headerPlaceholder: {
    width: 40,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  inputError: {
    borderColor: '#f44336',
    backgroundColor: '#ffebee',
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#999',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    marginTop: 4,
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  dateContainer: {
    flex: 1,
    marginRight: 10,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  checkboxLabel: {
    fontSize: 16,
    color: '#333',
  },
  providerScroll: {
    flexDirection: 'row',
  },
  providerChip: {
    backgroundColor: '#f0f0f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  providerChipSelected: {
    backgroundColor: '#2196F3',
    borderColor: '#2196F3',
  },
  providerChipText: {
    fontSize: 14,
    color: '#666',
  },
  providerChipTextSelected: {
    color: '#fff',
    fontWeight: '600',
  },
  categoryScroll: {
    flexDirection: 'row',
  },
  categoryChip: {
    backgroundColor: '#f0f0f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  categoryChipSelected: {
    backgroundColor: '#9C27B0',
    borderColor: '#9C27B0',
  },
  categoryChipText: {
    fontSize: 14,
    color: '#666',
  },
  categoryChipTextSelected: {
    color: '#fff',
    fontWeight: '600',
  },
  dynamicInputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  dynamicInputActions: {
    marginLeft: 12,
    justifyContent: 'center',
  },
  removeButton: {
    padding: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderStyle: 'dashed',
  },
  addButtonText: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '600',
    marginLeft: 8,
  },
  saveButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 40,
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default AddCertification;
